import React, { useState, useEffect } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { motion } from 'framer-motion'
import { toast } from '@/components/ui/use-toast'
import { supabase } from '@/lib/supabase'
import { authService } from '@/services/auth'
import { logEvent } from '@/lib/analytics'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAuth } from '@/contexts/AuthProvider'

const formSchema = z.object({
  // Use display_name instead of first_name/last_name for unified approach
  // This matches Google OAuth users and simplifies the schema
  displayName: z.string()
    .min(2, { message: 'Display name must be at least 2 characters.' })
    .max(100, { message: 'Display name cannot be longer than 100 characters.' }),
  email: z.string()
    .email({ message: 'Please enter a valid email address.' })
    .transform(email => email.toLowerCase()),
  password: z.string()
  .min(8, { message: 'Password must be at least 8 characters.' })
  .regex(/[a-zA-Z]/, { message: 'Include at least one letter.' })
  .regex(/[0-9]/, { message: 'Include at least one number.' }),

  confirmPassword: z.string(),
  phone: z.string()
    .regex(/^\+?\d{7,15}$/, { 
      message: 'Please enter a valid phone number (e.g., +37126123456)' 
    })
    .optional()
    .default(''),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the terms and privacy policy.'
  })
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

const SignUpPage = () => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    logEvent('signup_started')
  }, [])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      termsAccepted: false,
    },
  })

  // Optional: Extract common toast messages
  const TOAST_MESSAGES = {
    EMAIL_EXISTS: 'This email is already registered. Try logging in instead.',
    WEAK_PASSWORD: 'Password is too weak. Please choose a stronger password.',
    PROFILE_UPDATE_FAILED: 'Account created but profile details could not be saved. Please update in settings.',
    EMAIL_CONFIRMATION: 'Account created. Please check your email to confirm your address.'
  } as const

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true)
    try {
      await authService.signUp(values.email, values.password, true);
      // Store profile data for post-confirmation insert
      // NOTE: We use display_name (not first_name/last_name) for unified approach
      // This matches Google OAuth users and is stored in auth.users.raw_user_meta_data
      localStorage.setItem('signup-profile', JSON.stringify({
        email: values.email,
        display_name: values.displayName,
        phone_number: values.phone || '',
      }));
      localStorage.setItem('signup-email', values.email);
      navigate('/onboarding/check-email');
      setIsSubmitting(false);
      return;
    } catch (err: any) {
      console.error('Signup error:', err); // Debug log
      
      logEvent('signup_error', {
        error: err.message,
        formData: {
          displayName: values.displayName,
          email: values.email,
          phone: values.phone,
        }
      })

      let errorMessage = 'Signup failed'
      if (err.message?.includes('email')) {
        errorMessage = TOAST_MESSAGES.EMAIL_EXISTS
      } else if (err.message?.includes('password')) {
        errorMessage = TOAST_MESSAGES.WEAK_PASSWORD
      }

      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col p-6 pb-10">
      <button 
        onClick={() => navigate('/login', { replace: true })}
        className="self-start mb-6"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Create Your Account</h1>
        <p className="text-muted-foreground">Let's get your account set up</p>
      </motion.div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Name*</FormLabel>
                <FormControl><Input {...field} placeholder="How you'd like to be called" /></FormControl>
                <p className="text-sm text-muted-foreground mt-1">
                  This is how your name will appear in the app.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email*</FormLabel>
                <FormControl><Input type="email" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password*</FormLabel>
                <FormControl><Input type="password" {...field} /></FormControl>
                <p className="text-sm text-muted-foreground mt-1">
                  Must be at least 8 characters, include a letter and a number.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password*</FormLabel>
                <FormControl><Input type="password" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="+37126123456"
                    type="tel"
                  />
                </FormControl>
                <p className="text-sm text-muted-foreground mt-1">
                  Format: +37126123456
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="termsAccepted"
            render={({ field }) => (
              <FormItem className="flex items-start space-x-2">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div>
                  <FormLabel>
                    I agree to the{' '}
                    <span className="text-[#7097fa]">Terms of Service</span>
                    {' '}and{' '}
                    <span className="text-[#7097fa]">Privacy Policy</span>
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          <Button 
            type="submit" 
            className="w-full bg-lime text-black hover:bg-lime/90 py-6 rounded-xl" 
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <LoadingSpinner className="mr-2" />
                Creating Account...
              </span>
            ) : (
              'Create Account'
            )}
          </Button>
          <div className="text-center mt-4">
            <p className="text-muted-foreground">
              Already have an account?{' '}
              <Button 
                variant="link" 
                onClick={() => navigate('/login')}
                className="text-[#7097fa] font-medium p-0"
              >
                Login
              </Button>
            </p>
          </div>
        </form>
      </Form>
    </div>
  )
}

export default SignUpPage
